import React from "react";
import { Box, Card, Grid, Paper, Skeleton, Typography } from "@mui/material";

export const SectionTitle = ({ children, ...rest }) => (
  <Typography
    variant="subtitle1"
    fontWeight={700}
    fontSize={17}
    color="var(--app-text)"
    {...rest}
  >
    {children}
  </Typography>
);

// -------------------------------------------------------------------- InfoCard

const CARD_VARIANTS = {
  plain: {
    root: Paper,
    className: "card",
    sx: {
      backgroundColor: "var(--app-surface)",
      padding: "15px",
      cursor: "default",
    },
  },
  outlined: {
    root: Card,
    sx: {
      backgroundColor: "var(--app-surface)",
      border: "1px solid var(--app-border)",
      borderRadius: "12px",
      p: "17px",
    },
  },
  paper: {
    root: Paper,
    sx: {
      backgroundColor: "var(--app-surface)",
      border: "1px solid var(--app-border)",
      borderRadius: "12px",
      p: 2,
    },
  },
};

/** Card shell in one of the app's three surface styles. */
export const InfoCard = ({
  variant = "outlined",
  className,
  sx,
  children,
  ...rest
}) => {
  const preset = CARD_VARIANTS[variant] || CARD_VARIANTS.outlined;
  const Root = preset.root;
  const mergedClassName =
    [preset.className, className].filter(Boolean).join(" ") || undefined;

  return (
    <Root
      elevation={0}
      className={mergedClassName}
      sx={{ ...preset.sx, ...sx }}
      {...rest}
    >
      {children}
    </Root>
  );
};

// ----------------------------------------------------------------------- Field

const FIELD_LABEL_SX = {
  color: "var(--app-text-muted)",
  fontWeight: 800,
  letterSpacing: "0.08em",
  fontSize: "15px",
  fontFamily: "Figtree",
};

const FIELD_VALUE_SX = {
  fontWeight: 700,
  fontSize: "16px",
};

const FIELD_SKELETONS = {
  spaced: { width: "70%", height: 24, sx: { mt: 0.5 } },
  wave: { variant: "text", width: "70%", height: 28, animation: "wave" },
};

/** A read-only label above its value, with a skeleton while loading. */
export const Field = ({
  label,
  value,
  loading = false,
  stacked = false,
  skeleton = "spaced",
  labelSx,
  valueSx,
  valueProps,
}) => {
  const field = (
    <>
      <Typography variant="caption" sx={{ ...FIELD_LABEL_SX, ...labelSx }}>
        {label}
      </Typography>

      {loading ? (
        <Skeleton {...(FIELD_SKELETONS[skeleton] || FIELD_SKELETONS.spaced)} />
      ) : (
        <Typography
          variant="body1"
          {...valueProps}
          sx={{ ...FIELD_VALUE_SX, ...valueSx }}
        >
          {value}
        </Typography>
      )}
    </>
  );

  if (!stacked) return field;

  return (
    <Box display="flex" flexDirection="column">
      {field}
    </Box>
  );
};

// ------------------------------------------------------------------- FieldGrid


export const FieldGrid = ({
  items = [],
  loading = false,
  containerProps,
  itemProps,
  fieldProps,
}) => (
  <Grid container {...containerProps}>
    {items.map((item, index) => (
      <Grid
        item
        key={item.label ?? index}
        {...itemProps}
        onClick={item.onClick}
        sx={{
          ...itemProps?.sx,
          ...(item.onClick && { cursor: "pointer" }),
        }}
      >
        <Field
          label={item.label}
          value={item.value}
          loading={loading}
          {...fieldProps}
          valueSx={{
            ...fieldProps?.valueSx,
            ...(item.onClick && { textDecoration: "underline" }),
          }}
        />
      </Grid>
    ))}
  </Grid>
);

// -------------------------------------------------------------------- TextCard

const TEXT_CARD_SX = {
  fontWeight: 400,
  fontSize: 15,
  fontStyle: "regular",
  color: "var(--app-text)",
};

/** A section heading followed by a card holding a block of text. */
export const TextCard = ({
  title,
  text,
  variant = "paper",
  cardSx,
  titleProps,
  ...boxProps
}) => (
  <Box {...boxProps}>
    <SectionTitle {...titleProps}>{title}</SectionTitle>

    <InfoCard variant={variant} sx={cardSx}>
      <Typography variant="body2" sx={TEXT_CARD_SX}>
        {text}
      </Typography>
    </InfoCard>
  </Box>
);
